import type { EmployeeDto, SpecializationDto } from '@glory/contracts';
import { formatDate, formatMoney, interpolate, t, tp } from '@glory/i18n';
import { type Kopecks, kopecks } from '@glory/kernel';
import { clientHourlyRate, taxRulesOf } from '@glory/pricing/domain';
import {
  Badge,
  Button,
  Callout,
  Card,
  cn,
  ContentSwap,
  DateField,
  Dialog,
  DialogContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  IconButton,
  InfoHint,
  MoneyField,
  notify,
  PageHeader,
  SkeletonRows,
  SwitchField,
  TextField,
  toastUndoable,
} from '@glory/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Archive,
  CalendarPlus,
  ChevronDown,
  Layers,
  MoreHorizontal,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import { type SubmitEvent, useEffect, useState } from 'react';

import { client, orpc } from '../../../lib/orpc.ts';
import { usePermissions } from '../../../lib/permissions.ts';

interface SpecializationsSearch {
  readonly focus?: string | undefined;
}

export const Route = createFileRoute('/_app/settings/specializations')({
  validateSearch: (search: Record<string, unknown>): SpecializationsSearch => ({
    focus: typeof search.focus === 'string' ? search.focus : undefined,
  }),
  component: SpecializationsPage,
});

/** Средняя действующая личная ставка сотрудников с этой специализацией (до рубля). */
interface TeamAverage {
  readonly count: number;
  readonly rate: Kopecks;
}

function teamAverages(team: readonly EmployeeDto[]): ReadonlyMap<string, TeamAverage> {
  const sums = new Map<string, { count: number; sum: number }>();
  for (const employee of team) {
    const rate = employee.currentRate ?? null;
    if (!employee.active || rate === null) continue;
    for (const specialization of employee.specializations) {
      const entry = sums.get(specialization.id) ?? { count: 0, sum: 0 };
      entry.count += 1;
      entry.sum += rate;
      sums.set(specialization.id, entry);
    }
  }
  return new Map(
    [...sums].map(([id, { count, sum }]) => [
      id,
      { count, rate: kopecks(Math.round(sum / count / 100) * 100) },
    ]),
  );
}

type OpenDialog =
  | { readonly kind: 'create' }
  | { readonly kind: 'rate'; readonly specialization: SpecializationDto; readonly rateId: string | null }
  | { readonly kind: 'rename'; readonly specialization: SpecializationDto };

const useRefresh = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: orpc.org.specializations.key() });
};

function SpecializationsPage() {
  const { focus } = Route.useSearch();
  const { can } = usePermissions();
  const canManage = can('specializations.manage');
  const [showArchived, setShowArchived] = useState(false);
  const [dialog, setDialog] = useState<OpenDialog | null>(null);
  const list = useQuery(
    orpc.org.specializations.list.queryOptions({ input: { includeArchived: showArchived } }),
  );
  // Личные ставки команды видит только админ — ему рядом с плановой и средняя по людям.
  const seesTeamRates = can('rates.read.all');
  const team = useQuery({ ...orpc.org.team.list.queryOptions(), enabled: seesTeamRates });
  const averages = teamAverages(team.data?.items ?? []);
  // Цена часа для клиента — тем, кто видит цену: нужны действующие политика и налог.
  const seesPricing = can('pricing.read');
  const policies = useQuery({ ...orpc.pricing.policies.list.queryOptions(), enabled: seesPricing });
  const taxes = useQuery({ ...orpc.pricing.tax.list.queryOptions(), enabled: seesPricing });
  const policy = policies.data?.items.find((v) => v.status === 'current') ?? null;
  const taxProfile = taxes.data?.items.find((v) => v.status === 'current') ?? null;
  const clientRate =
    policy && taxProfile
      ? (rate: Kopecks) => clientHourlyRate({ costRatePerHour: rate, policy, tax: taxRulesOf(taxProfile) })
      : null;
  const clientRateMissing = seesPricing && policies.isSuccess && taxes.isSuccess && clientRate === null;
  const close = () => {
    setDialog(null);
  };

  const items = list.data?.items ?? [];
  const active = items.filter((s) => !s.archived);
  const archived = items.filter((s) => s.archived);
  const seeRates = items.some((s) => s.rates !== null);
  // Что под заголовком: загрузка, пустой список и список проявляются друг из друга, без скачка высоты.
  const phase = list.isPending ? 'loading' : list.isError ? 'error' : active.length === 0 ? 'empty' : 'ready';

  return (
    <div className="flex flex-col gap-4 px-4 py-5 md:px-6">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            {t('specializations.title')}
            <InfoHint hint={{ text: t('specializations.hint'), example: t('specializations.hintExample') }} />
          </span>
        }
        actions={
          canManage ? (
            <Button
              variant="primary"
              onClick={() => {
                setDialog({ kind: 'create' });
              }}
            >
              <Plus /> {t('specializations.create')}
            </Button>
          ) : null
        }
      />
      <p className="-mt-2 text-muted">
        {seeRates ? t('specializations.lead') : t('specializations.leadNames')}
      </p>
      {clientRateMissing ? (
        <p className="-mt-2 flex flex-wrap items-center gap-x-2 text-small text-muted">
          {t('specializations.clientRateMissing')}
          {policy === null ? (
            <Link to="/settings/pricing" className="font-semibold text-text underline underline-offset-2">
              {t('specializations.clientRateSetPolicy')}
            </Link>
          ) : null}
          {taxProfile === null ? (
            <Link to="/settings/tax" className="font-semibold text-text underline underline-offset-2">
              {t('specializations.clientRateSetTax')}
            </Link>
          ) : null}
        </p>
      ) : null}

      <ContentSwap swapKey={phase}>
        {list.isPending ? (
          <SkeletonRows rows={5} />
        ) : list.isError ? (
          <Callout>{list.error.message}</Callout>
        ) : active.length === 0 ? (
          <Card>
            <EmptyState
              icon={<Layers />}
              title={t('specializations.emptyTitle')}
              description={t('specializations.emptyText')}
              action={
                canManage ? (
                  <Button
                    variant="primary"
                    onClick={() => {
                      setDialog({ kind: 'create' });
                    }}
                  >
                    <Plus /> {t('specializations.create')}
                  </Button>
                ) : null
              }
            />
          </Card>
        ) : (
          <Card>
            <ul className="flex flex-col divide-y divide-border-soft">
              {active.map((specialization) => (
                <SpecializationRow
                  key={specialization.id}
                  specialization={specialization}
                  focused={focus === specialization.id}
                  average={averages.get(specialization.id) ?? null}
                  clientRate={clientRate}
                  canManage={canManage}
                  today={list.data.today}
                  onDialog={setDialog}
                />
              ))}
            </ul>
          </Card>
        )}
      </ContentSwap>

      {canManage ? (
        <div className="flex flex-col gap-2">
          <SwitchField
            inline
            label={t('specializations.showArchived')}
            checked={showArchived}
            onCheckedChange={setShowArchived}
          />
          {showArchived && archived.length > 0 ? (
            <Card>
              <ul className="flex flex-col divide-y divide-border-soft">
                {archived.map((specialization) => (
                  <ArchivedRow key={specialization.id} specialization={specialization} />
                ))}
              </ul>
            </Card>
          ) : null}
          {showArchived && archived.length === 0 && !list.isPending ? (
            <p className="text-small text-muted">{t('specializations.archiveEmpty')}</p>
          ) : null}
        </div>
      ) : null}

      <Dialog
        open={dialog !== null}
        onOpenChange={(open) => {
          if (!open) close();
        }}
      >
        {dialog?.kind === 'create' && list.data ? (
          <CreateDialog today={list.data.today} onDone={close} />
        ) : null}
        {dialog?.kind === 'rate' && list.data ? (
          <RateDialog
            specialization={dialog.specialization}
            rateId={dialog.rateId}
            today={list.data.today}
            onDone={close}
          />
        ) : null}
        {dialog?.kind === 'rename' ? (
          <RenameDialog specialization={dialog.specialization} onDone={close} />
        ) : null}
      </Dialog>
    </div>
  );
}

function SpecializationRow({
  specialization,
  focused,
  average,
  clientRate,
  canManage,
  today,
  onDialog,
}: {
  specialization: SpecializationDto;
  focused: boolean;
  average: TeamAverage | null;
  /** Цена часа для клиента по плановой ставке; null — не видно или не из чего считать. */
  clientRate: ((rate: Kopecks) => Kopecks) | null;
  canManage: boolean;
  today: string;
  onDialog: (dialog: OpenDialog) => void;
}) {
  const refresh = useRefresh();
  const [open, setOpen] = useState(focused);
  const [element, setElement] = useState<HTMLLIElement | null>(null);
  useEffect(() => {
    if (focused) element?.scrollIntoView({ block: 'center' });
  }, [focused, element]);

  const archive = useMutation({
    mutationFn: () =>
      client.org.specializations.archive({ id: specialization.id, version: specialization.version }),
    onSuccess: async () => {
      await refresh();
      toastUndoable({
        message: t('specializations.archived', { name: specialization.name }),
        onUndo: () =>
          void client.org.specializations
            .restore({ id: specialization.id, version: specialization.version + 1 })
            .then(refresh),
      });
    },
  });

  const rates = specialization.rates;
  const next = rates?.filter((r) => r.effectiveFrom > today).at(-1) ?? null;

  return (
    <li ref={setElement} className={cn('flex flex-col', focused && 'bg-accent/10')}>
      <div className="flex items-center gap-3 px-4 py-2.5">
        {rates ? (
          <IconButton
            label={open ? t('specializations.hideHistory') : t('specializations.showHistory')}
            icon={<ChevronDown className={cn('transition-transform', open && 'rotate-180')} />}
            size="sm"
            onClick={() => {
              setOpen((v) => !v);
            }}
          />
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate font-semibold">{specialization.name}</span>
          {next ? (
            <span className="text-caption text-muted">
              {t('specializations.next', {
                date: formatDate(next.effectiveFrom),
                rate: formatMoney(next.ratePerHour, { perHour: true }),
              })}
            </span>
          ) : null}
          {average ? (
            <span className="flex items-center gap-1 text-caption text-muted">
              {interpolate(tp('specializations.teamAverage', average.count), {
                rate: formatMoney(average.rate, { perHour: true }),
              })}
              <InfoHint hint={{ text: t('specializations.teamAverageHint') }} />
            </span>
          ) : null}
        </div>
        {rates ? (
          <span className="flex shrink-0 flex-col items-end">
            <span className="font-display font-bold tabular-nums">
              {specialization.currentRate === null ? (
                <span className="font-sans text-small font-semibold text-muted">
                  {t('specializations.noRate')}
                </span>
              ) : (
                formatMoney(specialization.currentRate, { perHour: true })
              )}
            </span>
            {clientRate && specialization.currentRate !== null ? (
              <span className="flex items-center gap-1 text-caption text-muted tabular-nums">
                {t('specializations.clientRate', {
                  rate: formatMoney(clientRate(specialization.currentRate), { perHour: true }),
                })}
                <InfoHint
                  hint={{
                    text: t('specializations.clientRateHint'),
                    example: t('specializations.clientRateExample'),
                  }}
                  label={t('fields.hintFor', { label: specialization.name })}
                />
              </span>
            ) : null}
          </span>
        ) : null}
        {canManage ? (
          <>
            <Button
              size="sm"
              className="hidden sm:inline-flex"
              onClick={() => {
                onDialog({ kind: 'rate', specialization, rateId: null });
              }}
            >
              {t('specializations.newRate')}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <IconButton label={t('specializations.more')} icon={<MoreHorizontal />} size="sm" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {/* На телефоне кнопке «Ставка с даты» нет места в строке — она здесь. */}
                <DropdownMenuItem
                  className="sm:hidden"
                  icon={<CalendarPlus />}
                  onSelect={() => {
                    onDialog({ kind: 'rate', specialization, rateId: null });
                  }}
                >
                  {t('specializations.newRate')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  icon={<Pencil />}
                  onSelect={() => {
                    onDialog({ kind: 'rename', specialization });
                  }}
                >
                  {t('specializations.rename')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  icon={<Archive />}
                  onSelect={() => {
                    archive.mutate();
                  }}
                >
                  {t('specializations.archive')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        ) : null}
      </div>
      {open && rates ? (
        <div className="border-t border-border-soft bg-surface-2/60 px-4 py-2.5 pl-14">
          {rates.length === 0 ? (
            <p className="text-small text-muted">{t('specializations.historyEmpty')}</p>
          ) : (
            <ol className="flex flex-col gap-1">
              {rates.map((rate) => {
                const inEffect = rate.id === rates.find((r) => r.effectiveFrom <= today)?.id;
                return (
                  <li key={rate.id} className="flex items-center gap-3 text-small">
                    <span className="w-28 shrink-0 text-muted tabular-nums">
                      {t('specializations.fromDate', { date: formatDate(rate.effectiveFrom) })}
                    </span>
                    <span className="font-semibold tabular-nums">
                      {formatMoney(rate.ratePerHour, { perHour: true })}
                    </span>
                    {inEffect ? <Badge tone="strong">{t('specializations.inEffect')}</Badge> : null}
                    {rate.editable ? <Badge tone="neutral">{t('specializations.scheduled')}</Badge> : null}
                    {rate.editable && canManage ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="ml-auto"
                        onClick={() => {
                          onDialog({ kind: 'rate', specialization, rateId: rate.id });
                        }}
                      >
                        <Pencil /> {t('specializations.edit')}
                      </Button>
                    ) : null}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      ) : null}
    </li>
  );
}

function ArchivedRow({ specialization }: { specialization: SpecializationDto }) {
  const refresh = useRefresh();
  const restore = useMutation({
    mutationFn: () =>
      client.org.specializations.restore({ id: specialization.id, version: specialization.version }),
    onSuccess: async () => {
      notify(t('specializations.restored', { name: specialization.name }));
      await refresh();
    },
  });
  return (
    <li className="flex items-center gap-3 px-4 py-2.5">
      <span className="min-w-0 flex-1 truncate text-muted">{specialization.name}</span>
      {restore.isError ? <span className="text-caption text-danger">{restore.error.message}</span> : null}
      <Button
        size="sm"
        loading={restore.isPending}
        onClick={() => {
          restore.mutate();
        }}
      >
        <RotateCcw /> {t('specializations.restore')}
      </Button>
    </li>
  );
}

function CreateDialog({ today, onDone }: { today: string; onDone: () => void }) {
  const refresh = useRefresh();
  const [name, setName] = useState('');
  const [rate, setRate] = useState<Kopecks | null>(null);
  const [from, setFrom] = useState<string | null>(today);
  const [nameError, setNameError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () =>
      client.org.specializations.create({
        name,
        ratePerHour: rate,
        effectiveFrom: rate === null ? null : from,
      }),
    onSuccess: async (created) => {
      notify(t('specializations.created', { name: created.name }));
      await refresh();
      onDone();
    },
  });
  const submit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (name.trim() === '') {
      setNameError(t('errors.org.specialization_name_required'));
      return;
    }
    create.mutate();
  };
  return (
    <DialogContent title={t('specializations.createTitle')} description={t('specializations.createLead')}>
      <form className="flex flex-col gap-3" noValidate onSubmit={submit}>
        <TextField
          label={t('specializations.name')}
          placeholder={t('specializations.namePlaceholder')}
          required
          autoFocus
          value={name}
          onValueChange={(value) => {
            setName(value);
            setNameError(null);
          }}
          error={nameError}
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <MoneyField
            label={t('specializations.rate')}
            description={t('specializations.rateHint')}
            value={rate}
            onValueChange={setRate}
          />
          <DateField label={t('specializations.from')} value={from} onValueChange={setFrom} min={today} />
        </div>
        {create.isError ? <Callout>{create.error.message}</Callout> : null}
        <div className="flex justify-end">
          <Button type="submit" variant="primary" loading={create.isPending}>
            {t('specializations.create')}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
}

function RateDialog({
  specialization,
  rateId,
  today,
  onDone,
}: {
  specialization: SpecializationDto;
  rateId: string | null;
  today: string;
  onDone: () => void;
}) {
  const refresh = useRefresh();
  const existing = specialization.rates?.find((r) => r.id === rateId) ?? null;
  const [rate, setRate] = useState<Kopecks | null>(existing?.ratePerHour ?? specialization.currentRate);
  const [from, setFrom] = useState<string | null>(existing?.effectiveFrom ?? null);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: ({ ratePerHour, effectiveFrom }: { ratePerHour: Kopecks; effectiveFrom: string }) =>
      existing
        ? client.org.specializations.changeRate({ rateId: existing.id, effectiveFrom, ratePerHour })
        : client.org.specializations.setRate({
            specializationId: specialization.id,
            effectiveFrom,
            ratePerHour,
          }),
    onSuccess: async () => {
      notify(t('specializations.rateSaved'));
      await refresh();
      onDone();
    },
  });
  const remove = useMutation({
    mutationFn: () => client.org.specializations.removeRate({ rateId: existing?.id ?? '' }),
    onSuccess: async () => {
      notify(t('specializations.rateRemoved'));
      await refresh();
      onDone();
    },
  });
  const submit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (rate === null || from === null) {
      setError(t('specializations.rateRequired'));
      return;
    }
    save.mutate({ ratePerHour: rate, effectiveFrom: from });
  };
  return (
    <DialogContent
      title={existing ? t('specializations.editRateTitle') : t('specializations.newRateTitle')}
      description={t('specializations.rateLead', { name: specialization.name })}
    >
      <form className="flex flex-col gap-3" noValidate onSubmit={submit}>
        <div className="grid gap-3 sm:grid-cols-2">
          <MoneyField
            label={t('specializations.rate')}
            required
            value={rate}
            onValueChange={(value) => {
              setRate(value);
              setError(null);
            }}
          />
          <DateField
            label={t('specializations.from')}
            description={t('specializations.fromHint')}
            required
            value={from}
            min={today}
            onValueChange={(value) => {
              setFrom(value);
              setError(null);
            }}
          />
        </div>
        {(error ?? save.error?.message ?? remove.error?.message) ? (
          <Callout>{error ?? save.error?.message ?? remove.error?.message}</Callout>
        ) : null}
        <div className="flex items-center justify-between gap-2">
          {existing ? (
            <Button
              type="button"
              variant="ghost"
              loading={remove.isPending}
              onClick={() => {
                remove.mutate();
              }}
            >
              <Trash2 /> {t('specializations.removeRate')}
            </Button>
          ) : (
            <span />
          )}
          <Button type="submit" variant="primary" loading={save.isPending}>
            {t('specializations.saveRate')}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
}

function RenameDialog({ specialization, onDone }: { specialization: SpecializationDto; onDone: () => void }) {
  const refresh = useRefresh();
  const [name, setName] = useState(specialization.name);
  const rename = useMutation({
    mutationFn: () =>
      client.org.specializations.rename({ id: specialization.id, version: specialization.version, name }),
    onSuccess: async () => {
      await refresh();
      onDone();
    },
  });
  return (
    <DialogContent title={t('specializations.renameTitle')}>
      <form
        className="flex flex-col gap-3"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          rename.mutate();
        }}
      >
        <TextField
          label={t('specializations.name')}
          required
          autoFocus
          value={name}
          onValueChange={setName}
        />
        {rename.isError ? <Callout>{rename.error.message}</Callout> : null}
        <div className="flex justify-end">
          <Button type="submit" variant="primary" loading={rename.isPending}>
            {t('common.save')}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
}
